import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { AbstractDialogHandler } from 'vscode/vscode/vs/platform/dialogs/common/dialogs';
import { ILayoutService } from 'vscode/vscode/vs/platform/layout/browser/layoutService.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';
import { Dialog } from 'vscode/vscode/vs/base/browser/ui/dialog/dialog';
import { DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { EventHelper } from 'vscode/vscode/vs/base/browser/dom';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { IProductService } from 'vscode/vscode/vs/platform/product/common/productService.service';
import { IClipboardService } from 'vscode/vscode/vs/platform/clipboard/common/clipboardService.service';
import { fromNow } from 'vscode/vscode/vs/base/common/date';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { MarkdownRenderer } from 'vscode/vscode/vs/editor/browser/widget/markdownRenderer/browser/markdownRenderer';
import { defaultButtonStyles, defaultCheckboxStyles, defaultInputBoxStyles, defaultDialogStyles } from 'vscode/vscode/vs/platform/theme/browser/defaultStyles';

var BrowserDialogHandler_1;
const _moduleId = "vs/workbench/browser/parts/dialogs/dialogHandler";
let BrowserDialogHandler = class BrowserDialogHandler extends AbstractDialogHandler {
    static { BrowserDialogHandler_1 = this; }
    static { this.ALLOWABLE_COMMANDS = [
        'copy',
        'cut',
        'editor.action.selectAll',
        'editor.action.clipboardCopyAction',
        'editor.action.clipboardCutAction',
        'editor.action.clipboardPasteAction'
    ]; }
    constructor(logService, layoutService, keybindingService, instantiationService, productService, clipboardService) {
        super();
        this.logService = logService;
        this.layoutService = layoutService;
        this.keybindingService = keybindingService;
        this.instantiationService = instantiationService;
        this.productService = productService;
        this.clipboardService = clipboardService;
        this.markdownRenderer = this.instantiationService.createInstance(MarkdownRenderer, {});
    }
    async prompt(prompt) {
        this.logService.trace('DialogService#prompt', prompt.message);
        const buttons = this.getPromptButtons(prompt);
        const { button, checkboxChecked } = await this.doShow(prompt.type, prompt.message, buttons, prompt.detail, prompt.cancelButton ? buttons.length - 1 : -1 , prompt.checkbox, undefined, typeof prompt?.custom === 'object' ? prompt.custom : undefined);
        return this.getPromptResult(prompt, button, checkboxChecked);
    }
    async confirm(confirmation) {
        this.logService.trace('DialogService#confirm', confirmation.message);
        const buttons = this.getConfirmationButtons(confirmation);
        const { button, checkboxChecked } = await this.doShow(confirmation.type ?? 'question', confirmation.message, buttons, confirmation.detail, buttons.length - 1, confirmation.checkbox, undefined, typeof confirmation?.custom === 'object' ? confirmation.custom : undefined);
        return { confirmed: button === 0, checkboxChecked };
    }
    async input(input) {
        this.logService.trace('DialogService#input', input.message);
        const buttons = this.getInputButtons(input);
        const { button, checkboxChecked, values } = await this.doShow(input.type ?? 'question', input.message, buttons, input.detail, buttons.length - 1, input?.checkbox, input.inputs, typeof input.custom === 'object' ? input.custom : undefined);
        return { confirmed: button === 0, checkboxChecked, values };
    }
    async about() {
        const detailString = (useAgo) => {
            return ( localizeWithPath(
                _moduleId,
                0,
                "Version: {0}\nCommit: {1}\nDate: {2}\nBrowser: {3}",
                this.productService.version || 'Unknown',
                this.productService.commit || 'Unknown',
                this.productService.date ? `${this.productService.date}${useAgo ? ' (' + fromNow(( (new Date(this.productService.date))), true) + ')' : ''}` : 'Unknown',
                navigator.userAgent
            ));
        };
        const detail = detailString(true);
        const detailToCopy = detailString(false);
        const { button } = await this.doShow(Severity$1.Info, this.productService.nameLong, [
            ( localizeWithPath(_moduleId, 1, "&&Copy")),
            ( localizeWithPath(_moduleId, 2, "OK"))
        ], detail, 1);
        if (button === 0) {
            this.clipboardService.writeText(detailToCopy);
        }
    }
    async doShow(type, message, buttons, detail, cancelId, checkbox, inputs, customOptions) {
        const dialogDisposables = ( (new DisposableStore()));
        const renderBody = customOptions ? (parent) => {
            parent.classList.add(...(customOptions.classes || []));
            customOptions.markdownDetails?.forEach(markdownDetail => {
                const result = this.markdownRenderer.render(markdownDetail.markdown);
                parent.appendChild(result.element);
                result.element.classList.add(...(markdownDetail.classes || []));
                dialogDisposables.add(result);
            });
        } : undefined;
        const dialog = ( (new Dialog(this.layoutService.activeContainer, message, buttons, {
            detail,
            cancelId,
            type: this.getDialogType(type),
            keyEventProcessor: (event) => {
                const resolved = this.keybindingService.softDispatch(event, this.layoutService.activeContainer);
                if (resolved.kind === 2  && resolved.commandId) {
                    if (BrowserDialogHandler_1.ALLOWABLE_COMMANDS.indexOf(resolved.commandId) === -1) {
                        EventHelper.stop(event, true);
                    }
                }
            },
            renderBody,
            icon: customOptions?.icon,
            disableCloseAction: customOptions?.disableCloseAction,
            buttonDetails: customOptions?.buttonDetails,
            checkboxLabel: checkbox?.label,
            checkboxChecked: checkbox?.checked,
            inputs,
            buttonStyles: defaultButtonStyles,
            checkboxStyles: defaultCheckboxStyles,
            inputBoxStyles: defaultInputBoxStyles,
            dialogStyles: defaultDialogStyles
        })));
        dialogDisposables.add(dialog);
        const result = await dialog.show();
        dialogDisposables.dispose();
        return result;
    }
};
BrowserDialogHandler = BrowserDialogHandler_1 = ( (__decorate([
    ( (__param(0, ILogService))),
    ( (__param(1, ILayoutService))),
    ( (__param(2, IKeybindingService))),
    ( (__param(3, IInstantiationService))),
    ( (__param(4, IProductService))),
    ( (__param(5, IClipboardService)))
], BrowserDialogHandler)));

export { BrowserDialogHandler };
